import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, X, Heart, ChevronDown, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCapsuleFavorites } from '../hooks/useCapsuleFavorites';
import { getAllCapsules, getTrendingCapsules } from '../utils/supabaseDatabase';
import { useSolPrice } from '../contexts/SolPriceContext';

const Capsules = () => {
  const navigate = useNavigate();
  const { toggleCapsuleFavorite, isCapsuleFavorited } = useCapsuleFavorites();
  const { solPrice } = useSolPrice();
  
  const [capsules, setCapsules] = useState([]);
  const [featuredCapsules, setFeaturedCapsules] = useState([]);
  const [capsulesLoading, setCapsulesLoading] = useState(true);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [capsulePage, setCapsulePage] = useState(0);
  const [featuredCapsulePage, setFeaturedCapsulePage] = useState(0);
  
  // Dropdown refs
  const sortDropdownRef = useRef(null);

  const sortOptions = [
    { id: 'newest', name: 'Newest' },
    { id: 'oldest', name: 'Oldest' },
    { id: 'most-pieces', name: 'Most Pieces' },
    { id: 'least-pieces', name: 'Least Pieces' },
    { id: 'alphabetical', name: 'Alphabetical (A-Z)' },
  ];


  // Load trending capsules for featured section (same as Explore page)
  const loadFeaturedCapsules = async () => {
    try {
      setFeaturedLoading(true);
      console.log('🔄 Capsules: Loading trending capsules for featured section...');
      const trendingCapsulesData = await getTrendingCapsules(12); // Get 12 for featured carousel
      console.log('✅ Capsules: Loaded trending capsules:', trendingCapsulesData);
      console.log('📊 Capsules: Number of trending capsules:', trendingCapsulesData?.length || 0);
      
      if (trendingCapsulesData && trendingCapsulesData.length > 0) {
        console.log('🎯 Capsules: Using trending capsules for featured section');
        setFeaturedCapsules(trendingCapsulesData);
      } else {
        console.log('⚠️ Capsules: No trending capsules found, using all capsules');
        // Fallback to all capsules if no trending data
        const capsulesData = await getAllCapsules(0, 12);
        setFeaturedCapsules(capsulesData?.capsules || []);
      }
    } catch (error) {
      console.error('❌ Capsules: Error loading trending capsules:', error);
      console.log('🔄 Capsules: Falling back to all capsules');
      // Fallback to all capsules if trending fails
      try {
        const capsulesData = await getAllCapsules(0, 12);
        setFeaturedCapsules(capsulesData?.capsules || []);
      } catch (fallbackError) {
        setFeaturedCapsules([]);
      }
    } finally {
      setFeaturedLoading(false);
    }
  };

  // Load all capsules for the main grid
  const loadCapsules = async () => {
    try {
      setCapsulesLoading(true);
      console.log('🔄 Capsules: Loading all capsules from Supabase...');
      const capsulesData = await getAllCapsules();
      console.log('✅ Capsules: Loaded capsules from Supabase:', capsulesData);
      console.log('📊 Capsules: Number of capsules:', capsulesData?.capsules?.length || 0);
      
      if (capsulesData?.capsules && capsulesData.capsules.length > 0) {
        console.log('🎯 Capsules: Using real Supabase data');
        setCapsules(capsulesData.capsules);
      } else {
        console.log('⚠️ Capsules: No capsules found');
        setCapsules([]);
      }
    } catch (error) {
      console.error('❌ Capsules: Error loading capsules:', error);
      console.log('🔄 Capsules: No data available');
      // Set empty array if Supabase fails
      setCapsules([]);
    } finally {
      setCapsulesLoading(false);
    }
  };

  useEffect(() => {
    loadFeaturedCapsules(); // Load trending capsules for featured section
    loadCapsules(); // Load all capsules for main grid
  }, []);

  // Filter and sort capsules
  const getFilteredCapsules = () => {
    let filtered = [...capsules];

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(capsule =>
        capsule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        capsule.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.created_at || a.createdAt) - new Date(b.created_at || b.createdAt));
        break;
      case 'most-pieces':
        filtered.sort((a, b) => (b.pieceCount || b.piece_count || 0) - (a.pieceCount || a.piece_count || 0));
        break;
      case 'least-pieces':
        filtered.sort((a, b) => (a.pieceCount || a.piece_count || 0) - (b.pieceCount || b.piece_count || 0));
        break;
      case 'alphabetical':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredCapsules = getFilteredCapsules();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#000000' }}>
      <div className="max-w-[1800px] mx-auto px-6 py-8">

        {/* Featured Capsules Carousel - Using Trending Logic */}
        {!featuredLoading && featuredCapsules.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Trending Capsules</h3>
                <p className="text-gray-400">Most popular collections right now</p>
              </div>
            </div>
            
            <div className="relative overflow-hidden">
              {/* Mobile: Horizontal scroll */}
              <div className="md:hidden overflow-x-auto scrollbar-hide">
                <div className="flex gap-4 pb-4 px-4">
                  {featuredCapsules.slice(0, 12).map((capsule) => (
                    <div 
                      key={capsule.id} 
                      className="w-[280px] flex-shrink-0 group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 aspect-[420/320]"
                    >
                      <div 
                        onClick={() => navigate(`/capsule/${capsule.id}`)}
                        className="cursor-pointer group relative h-full flex flex-col"
                      >
                        {/* Banner Image - Top half */}
                        <div className="relative h-1/2 bg-[#2a2a2a] flex items-center justify-center overflow-hidden">
                          {capsule.banner ? (
                            <img 
                              src={capsule.banner} 
                              alt={capsule.name}
                              className="w-full h-full object-cover"
                            />
                          ) : capsule.image ? (
                            <img 
                              src={capsule.image} 
                              alt={capsule.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <img 
                              src="https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=300&h=200&fit=crop" 
                              alt={capsule.name}
                              className="w-full h-full object-cover"
                            />
                          )}

                          {/* Heart Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCapsuleFavorite(capsule);
                            }}
                            className="absolute top-3 right-3 p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                          >
                            <Heart 
                              size={16} 
                              className={`${
                                isCapsuleFavorited(capsule.id) 
                                  ? 'text-red-500 fill-red-500' 
                                  : 'text-white/70 hover:text-red-500'
                              } transition-all duration-300`} 
                            />
                          </button>
                        </div>

                        {/* Bottom Section */}
                        <div className="h-1/2 p-3 flex flex-col justify-between">
                          <div className="space-y-1">
                            <h3 className="text-white font-bold text-sm line-clamp-1 group-hover:text-gray-200 transition-colors">
                              {capsule.name}
                            </h3>
                            <p className="text-gray-400 text-xs line-clamp-2 leading-relaxed">
                              {capsule.description}
                            </p>
                            <div className="text-gray-400 text-xs">
                              • {capsule.pieceCount || capsule.piece_count || 0} {(capsule.pieceCount || capsule.piece_count || 0) === 1 ? 'piece' : 'pieces'}
                            </div>
                          </div>

                          <div className="space-y-1">
                            {(() => {
                              const price = parseFloat(capsule.avgPrice || capsule.price || 0);
                              return price > 0 ? (
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-gray-400 text-xs flex-shrink-0">Avg Price</div>
                                  <div className="text-right flex-shrink-0">
                                    <div className="text-green-400 font-semibold text-xs whitespace-nowrap">
                                      {price < 0.1 ? price.toFixed(3) : price.toFixed(1)} SOL
                                    </div>
                                    {solPrice && (
                                      <div className="text-gray-500 text-[10px] whitespace-nowrap">
                                        ${(price * solPrice).toFixed(2)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : null;
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Desktop: Paginated carousel */}
              <div className="hidden md:block">
                <div 
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${featuredCapsulePage * 100}%)` }}
                >
                  {[0, 1, 2].map((pageIndex) => (
                    <div key={pageIndex} className="w-full flex-shrink-0">
                      <div className="grid gap-6 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {(pageIndex === 0 ? featuredCapsules.slice(0, 4) :
                        pageIndex === 1 ? featuredCapsules.slice(4, 8) :
                        featuredCapsules.slice(8, 12)).map((capsule) => (
                        <div 
                          key={capsule.id} 
                          className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 aspect-[420/320]"
                        >
                          <div 
                            onClick={() => navigate(`/capsule/${capsule.id}`)}
                            className="cursor-pointer group relative h-full flex flex-col"
                          >
                            {/* Banner Image - Top half */}
                            <div className="relative h-1/2 bg-[#2a2a2a] flex items-center justify-center overflow-hidden">
                              {capsule.banner ? (
                                <img 
                                  src={capsule.banner} 
                                  alt={capsule.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : capsule.image ? (
                                <img 
                                  src={capsule.image} 
                                  alt={capsule.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <img 
                                  src="https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=300&h=200&fit=crop" 
                                  alt={capsule.name}
                                  className="w-full h-full object-cover"
                                />
                              )}

                              {/* Heart Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleCapsuleFavorite(capsule);
                                }}
                                className="absolute top-3 right-3 p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                              >
                                <Heart 
                                  size={16} 
                                  className={`${
                                    isCapsuleFavorited(capsule.id) 
                                      ? 'text-red-500 fill-red-500' 
                                      : 'text-white/70 hover:text-red-500'
                                  } transition-all duration-300`} 
                                />
                              </button>
                            </div>

                            {/* Bottom Section with Info - Bottom half */}
                            <div className="h-1/2 p-3 flex flex-col justify-between">
                              <div className="space-y-1">
                                {/* Title */}
                                <h3 className="text-white font-bold text-sm line-clamp-1 group-hover:text-gray-200 transition-colors">
                                  {capsule.name}
                                </h3>

                                {/* Description */}
                                <p className="text-gray-400 text-xs line-clamp-2 leading-relaxed">
                                  {capsule.description}
                                </p>

                                {/* Pieces Count */}
                                <div className="text-gray-400 text-xs">
                                  • {capsule.pieceCount || capsule.piece_count || 0} {(capsule.pieceCount || capsule.piece_count || 0) === 1 ? 'piece' : 'pieces'}
                                </div>
                              </div>

                              <div className="space-y-1">
                                {/* Avg Price Row */}
                                {(() => {
                                  const price = parseFloat(capsule.avgPrice || capsule.price || 0);
                                  return price > 0 ? (
                                    <div className="flex items-center justify-between">
                                      <div className="text-gray-400 text-xs">Avg Price</div>
                                      <div className="text-right">
                                        <div className="text-green-400 font-semibold text-sm">
                                          {price < 0.1 ? price.toFixed(3) : price.toFixed(1)} SOL
                                        </div>
                                        <div className="text-gray-500 text-xs">
                                          ~${(price * solPrice).toFixed(2)} USD
                                        </div>
                                      </div>
                                    </div>
                                  ) : null;
                                })()}

                                {/* Drop Time */}
                                {(capsule.dropped_at || capsule.created_at) && (
                                  <div className="text-gray-500 text-xs">
                                    Dropped {new Date(capsule.dropped_at || capsule.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination Dots - Desktop only */}
              <div className="flex justify-center space-x-2 mt-6">
              {[0, 1, 2].map((page) => (
                <button
                  key={page}
                  onClick={() => setFeaturedCapsulePage(page)}
                  className={`w-8 h-2 rounded-full transition-all duration-200 ${
                    featuredCapsulePage === page
                      ? 'bg-white'
                      : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                />
              ))}
              </div>
            </div>
            </div>
          </section>
        )}

        {/* Search and Filter Bar */}
        {!capsulesLoading && capsules.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-4">
              {/* Search Bar */}
              <div className="flex-1 relative">
                <Search className="hidden md:block absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search capsules..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-4 md:pl-12 pr-4 py-3 bg-[#1a1a1a] border border-white/30 rounded-xl text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>

              {/* Sort Dropdown */}
              <div className="relative" ref={sortDropdownRef}>
                <button
                  onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                  className="flex items-center gap-2 px-4 py-3 bg-[#1a1a1a] border border-white/30 rounded-xl text-white hover:bg-[#2a2a2a] transition-all"
                >
                  <Filter size={18} />
                  <span className="text-sm font-medium">
                    {sortOptions.find(opt => opt.id === sortBy)?.name || 'Sort By'}
                  </span>
                  <ChevronDown size={16} className={`transition-transform ${isSortDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isSortDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-[#1a1a1a] border border-white/30 rounded-xl shadow-xl overflow-hidden z-50">
                    {sortOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => {
                          setSortBy(option.id);
                          setIsSortDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                          sortBy === option.id
                            ? 'bg-white/10 text-white'
                            : 'text-gray-300 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* All Capsules Section Header */}
        {!capsulesLoading && filteredCapsules.length > 0 && (
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-white mb-2">All Capsules</h3>
            <p className="text-gray-400">Browse all available capsule collections</p>
          </div>
        )}

        {/* Loading State */}
        {capsulesLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-white text-lg">Loading capsules...</div>
          </div>
        )}

        {/* Empty State */}
        {!capsulesLoading && filteredCapsules.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20">
            <Package className="w-16 h-16 text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No capsules found</h3>
            <p className="text-gray-400 text-center max-w-md">
              {searchQuery
                ? "Try adjusting your search or filters"
                : "No capsules have been created yet"}
            </p>
          </div>
        )}

        {/* Capsules Grid */}
        {!capsulesLoading && filteredCapsules.length > 0 && (
          <div className="grid gap-6 grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredCapsules.map((capsule) => (
              <div 
                key={capsule.id} 
                className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 aspect-[420/320]"
              >
                <div 
                  onClick={() => navigate(`/capsule/${capsule.id}`)}
                  className="cursor-pointer group relative h-full flex flex-col"
                >
                  {/* Banner Image - Top half */}
                  <div className="relative h-1/2 bg-[#2a2a2a] flex items-center justify-center overflow-hidden">
                    {capsule.banner ? (
                      <img 
                        src={capsule.banner} 
                        alt={capsule.name}
                        className="w-full h-full object-cover"
                      />
                    ) : capsule.image ? (
                      <img 
                        src={capsule.image} 
                        alt={capsule.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img 
                        src="https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=300&h=200&fit=crop" 
                        alt={capsule.name}
                        className="w-full h-full object-cover"
                      />
                    )}

                    {/* Heart Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCapsuleFavorite(capsule);
                      }}
                      className="absolute top-3 right-3 p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                    >
                      <Heart 
                        size={16} 
                        className={`${
                          isCapsuleFavorited(capsule.id) 
                            ? 'text-red-500 fill-red-500' 
                            : 'text-white/70 hover:text-red-500'
                        } transition-all duration-300`} 
                      />
                    </button>
                  </div>

                  {/* Bottom Section with Info - Bottom half */}
                  <div className="h-1/2 p-3 flex flex-col justify-between">
                    <div className="space-y-1">
                      {/* Title */}
                      <h3 className="text-white font-bold text-sm line-clamp-1 group-hover:text-gray-200 transition-colors">
                        {capsule.name}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-400 text-xs line-clamp-2 leading-relaxed">
                        {capsule.description}
                      </p>

                      {/* Pieces Count */}
                      <div className="text-gray-400 text-xs">
                        • {capsule.pieceCount || capsule.piece_count || 0} {(capsule.pieceCount || capsule.piece_count || 0) === 1 ? 'piece' : 'pieces'}
                      </div>
                    </div>

                    <div className="space-y-1">
                      {/* Avg Price Row */}
                      {(capsule.avgPrice || capsule.price) && (
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-gray-400 text-xs flex-shrink-0">Avg Price</div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-green-400 font-semibold text-xs md:text-sm whitespace-nowrap">
                              {(() => {
                                const price = parseFloat(capsule.avgPrice || capsule.price || 0);
                                // Show more precision for prices < 0.1 SOL
                                if (price > 0 && price < 0.1) {
                                  return price.toFixed(3);
                                }
                                return isNaN(price) ? '0.0' : price.toFixed(1);
                              })()} SOL
                            </div>
                            <div className="text-gray-500 text-[10px] md:text-xs whitespace-nowrap">
                              {(() => {
                                const price = parseFloat(capsule.avgPrice || capsule.price || 0);
                                const usdPrice = isNaN(price) ? 0 : price * solPrice;
                                return usdPrice > 0 ? `~$${usdPrice.toFixed(2)} USD` : '';
                              })()}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Drop Time */}
                      {(capsule.dropped_at || capsule.created_at) && (
                        <div className="text-gray-500 text-xs">
                          Dropped {new Date(capsule.dropped_at || capsule.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Capsules;

